/**
 * 🚨 404 ERROR PAGE
 * Professional "Page Not Found" component
 * Provides clear guidance and navigation options
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as styles from './errorPages.css';

interface NotFoundPageProps {
  /** Optional custom message */
  message?: string;
  /** Optional path that was attempted */
  attemptedPath?: string;
  /** Show technical details toggle */
  showTechnicalDetails?: boolean;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({
  message,
  attemptedPath,
  showTechnicalDetails = false
}) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = React.useState(false);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const suggestions = [
    'Vérifiez l\'URL saisie pour détecter d\'éventuelles erreurs de frappe',
    'Utilisez la navigation principale pour explorer le portfolio',
    'Recherchez le contenu avec la fonction de recherche',
    'Revenez à la page d\'accueil et naviguez depuis là'
  ];

  return (
    <div className={styles.errorContainer}>
      {/* Breadcrumb for context */}
      <nav className={styles.breadcrumb}>
        <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
        <span> / </span>
        <span>Erreur 404</span>
      </nav>

      <div className={styles.errorContent}>
        {/* Error Icon */}
        <div className={styles.errorIcon}>
          🔍
        </div>

        {/* Error Code */}
        <h1 className={styles.errorCode}>
          404
          <span className={styles.srOnly}>Erreur quatre cent quatre</span>
        </h1>

        {/* Error Title */}
        <h2 className={styles.errorTitle}>
          Page Introuvable
        </h2>

        {/* Error description */}
        <p className={styles.errorDescription}>
          {message || 
            "Désolé, la page que vous recherchez n'existe pas ou a été déplacée. Elle a peut-être été supprimée, renommée ou est temporairement indisponible."
          }
        </p>

        {/* Suggestions */}
        <div>
          <h3 style={{ 
            fontSize: '1.1rem', 
            marginBottom: '1rem', 
            color: 'var(--colors-text)',
            fontWeight: 600 
          }}>
            Que pouvez-vous faire ?
          </h3>
          <ul className={styles.suggestionsList}>
            {suggestions.map((suggestion, index) => (
              <li key={index} className={styles.suggestionItem}>
                {suggestion}
              </li>
            ))}
          </ul>
        </div>

        {/* action Buttons */}
        <div className={styles.actionButtons}>
          <Link to="/" className={styles.primaryButton}>
            <span>🏠</span>
            Retour à l'accueil
          </Link>
          <button 
            onClick={handleGoBack}
            className={styles.secondaryButton}
          >
            <span>⬅️</span>
            Page précédente
          </button>
        </div>

        {/* Status Indicator */}
        <div className={styles.statusIndicator}>
          <div className={styles.statusDot}></div>
          <span>Tous les systèmes opérationnels</span>
        </div>

        {/* Technical Details (if enabled) */}
        {showTechnicalDetails && (
          <div className={styles.technicalDetails}>
            <h4 
              className={styles.technicalTitle}
              onClick={() => setShowDetails(!showDetails)}
            >
              <span>{showDetails ? '🔽' : '▶️'}</span>
              Détails techniques
            </h4>
            {showDetails && (
              <div className={styles.technicalContent}>
                {`Erreur: 404 Not Found
Chemin demandé: ${attemptedPath || window.location.pathname}
Timestamp: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
Referrer: ${document.referrer || 'Direct access'}`}
              </div>
            )}
          </div>
        )}

        {/* Help section */}
        <div className={styles.helpSection}>
          <h3 className={styles.helpTitle}>
            Besoin d'aide ?
          </h3>
          <p className={styles.helpText}>
            Si vous pensez qu'il s'agit d'une erreur ou si vous avez besoin d'assistance, 
            n'hésitez pas à me contacter.
          </p>
          <div className={styles.contactInfo}>
            <a href="mailto:contact@yourportfolio.com" className={styles.contactLink}>
              📧 contact@yourportfolio.com
            </a>
            <a href="HTTPS://linkedin.com/in/yourprofile" className={styles.contactLink}>
              💼 LinkedIn
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
